import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import {
  ColumnDef,
  OnChangeFn,
  flexRender,
  useReactTable,
  getCoreRowModel,
  ColumnOrderState,
} from '@tanstack/react-table';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import { Button } from 'ncoded-component-library';
import useQueryParams from 'hooks/useQueryParams';
import { ReorderResizeTableProps, ResCellContext, RowType } from './types';
import SortIcon from 'icons/Sort.icon';
import DraggableCard from 'components/DraggableCard';
import ResolvePagination from 'components/ResolvePagination';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { hasSettings, isSortableCol } from './utils';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import TableRowPlaceholder from './components/TableRowPlaceholder';
import {
  getIsSortedDescBy,
  getIsSortApplied,
  getSortValue,
  getArrayLength,
} from 'utils';
import TableEmptyState from './components/TableEmptyState';
import useMatchMedia from 'hooks/useMatchMedia';
import SettingsIcon from 'icons/Settings.icon';
import useHorizontalScrollFade from 'hooks/useHorizontalScrollFade';
import ColumnSortFilterDropdown from 'components/ColumnSortFilterDropdown';

import './ReorderResizeTable.styles.scss';

const DEFAULT_COL = {
  size: 200, //starting column size
  minSize: 50, //enforced during column resizing
  maxSize: 1000, //enforced during column resizing
};

const DRAG_START_DISTANCE = 6;

function ReorderResizeTable<T extends Record<string, any>>(
  props: ReorderResizeTableProps<T>,
) {
  const {
    params: { sort: paramsSort },
    setQueryParam,
  } = useQueryParams<{ sort: string; page: string }>();

  const {
    className,
    data,
    columns,
    headingTranslations,
    tableContentTransformations,
    sortingValues,
    columnFilterOptions,
    settingsValues,
    settingsButtonClassName,
    deafaultTableColSize,
    customSizes,
    tableHeaderContentTransformations,
    isDragDisabled,
    isResizeDisabled,
    emptyMessage,
    pageParam,
    settingsButtonId,
    totalPages = 1,
    numberOfPlaceholderRows = 10,
    showPlaceholder = true,
    isDataLoading = false,
    hasHorizontalScrollFade = true,
    columnResizeMode = 'onChange',
    columnResizeDirection = 'ltr',
    sort = paramsSort,
    onReorder,
    getIsRowExpanded,
    getIsRowSelected,
    getRowBackgroundColor,
    getColSpan,
    renderExpandedContent,
    onRowClick,
    onRowDoubleClick,
    updateSort = (value: string) => setQueryParam('sort', value),
  } = props;

  const numberOfPlaceholderRowsToRender = useMemo(() => {
    const numberOfRows = getArrayLength(data);

    if (
      totalPages === 1 &&
      !!numberOfRows &&
      numberOfRows < numberOfPlaceholderRows
    )
      return numberOfRows;

    return numberOfPlaceholderRows;
  }, [data, numberOfPlaceholderRows, totalPages]);

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const reorderCB: OnChangeFn<ColumnOrderState> = useCallback(
    (v) => onReorder?.(v as string[]),
    [onReorder],
  );

  const headingTranslationsMap = useMemo(
    () =>
      headingTranslations
        ? Object.entries(headingTranslations).reduce(
            (acc, [key, val]) => ({ ...acc, [key]: t(val) }),
            {} as Record<keyof T, string>,
          )
        : columns.reduce(
            (acc, col) => ({ ...acc, [col]: t(col as string) }),
            {} as Record<keyof T, string>,
          ),
    [headingTranslations, columns, t],
  );

  // Use a ref for header transformations so that column definitions
  // (and cell functions) stay stable when only headers change.
  // This is critical because TanStack's flexRender treats cell functions
  // as React components — a new function reference causes unmount/remount.
  const tableHeaderTransformationsRef = useRef(
    tableHeaderContentTransformations,
  );
  tableHeaderTransformationsRef.current = tableHeaderContentTransformations;

  const tanstackColumns = useMemo(
    () =>
      (columns as string[]).map((col) => ({
        header: (() => {
          const transform = tableHeaderTransformationsRef.current?.[col];
          if (!transform) return headingTranslationsMap[col];
          if (typeof transform !== 'function') return transform;
          // Stable wrapper that delegates to the latest header transformation via ref.
          // This prevents column recreation when only headers change (e.g. isAllSelected).
          return (val?: any) => {
            const current = tableHeaderTransformationsRef.current?.[col];
            return typeof current === 'function' ? current(val) : current;
          };
        })(),
        accessorKey: col,
        cell: (info: ResCellContext<RowType, string>) =>
          tableContentTransformations[col]?.(info.lead, info.config),
        ...(customSizes?.[col] ? { size: customSizes[col] } : {}),
      })) as unknown as ColumnDef<T>[],
    [columns, headingTranslationsMap, tableContentTransformations, customSizes],
  );

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    columnResizeMode,
    columnResizeDirection,
    getCoreRowModel: getCoreRowModel(),
    getIsRowExpanded,
    initialState: {
      columnOrder: columns as string[],
    },
    state: {
      columnOrder: columns as string[],
    },
    onColumnOrderChange: reorderCB,
    defaultColumn: deafaultTableColSize || DEFAULT_COL,
  });

  const [currentlyDragged, setCurrentlyDragged] = useState('');

  const shouldSkipNextSortRef = useRef(false);

  const numberOfColumns = table.getAllColumns().length;

  const numberOfRows = table.getRowCount();

  const showFade = hasHorizontalScrollFade && !isDataLoading;

  const { showLeftFade, showRightFade, scrollContainerRef } =
    useHorizontalScrollFade(showFade, [
      data,
      columns,
      isDataLoading,
      numberOfRows,
    ]);

  const isEmptyTable = useMemo(() => !numberOfRows, [numberOfRows]);

  const shouldShowPlaceholder = isDataLoading && showPlaceholder;

  const handleDragStart = useCallback((e) => {
    shouldSkipNextSortRef.current = true;
    setCurrentlyDragged(e.active.id as string);
  }, []);

  const clearDragState = useCallback(() => {
    setCurrentlyDragged('');
    setTimeout(() => {
      shouldSkipNextSortRef.current = false;
    }, 0);
  }, []);

  const onDragEnd = useCallback(
    async (e: DragEndEvent) => {
      const { active, over } = e;

      clearDragState();

      if (!over || active.id === over.id) return;

      const oldIndex = columns.findIndex((old) => old === active.id);
      const newIndex = columns.findIndex((old) => old === over.id);

      await onReorder?.(arrayMove(columns, oldIndex, newIndex) as any);
    },
    [columns, onReorder, clearDragState],
  );

  const onSort = useCallback(
    (id: string, isDesc: boolean) => {
      if (shouldSkipNextSortRef.current) {
        shouldSkipNextSortRef.current = false;
        return;
      }

      const sortingValue = sortingValues[id];

      updateSort(getSortValue(sortingValue, !isDesc));
    },
    [sortingValues, updateSort],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: DRAG_START_DISTANCE,
      },
    }),
  );

  const classes = classNames(
    'reorder-resize-table',
    { loading: isDataLoading, empty: isEmptyTable },
    className,
  );

  if (isPhablet && isEmptyTable && !isDataLoading)
    return <TableEmptyState emptyMessage={emptyMessage} />;

  return (
    <div
      className={classes}
      style={{ direction: table.options.columnResizeDirection }}
    >
      <DndContext
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragEnd={onDragEnd}
        onDragStart={handleDragStart}
        onDragCancel={clearDragState}
        modifiers={[restrictToHorizontalAxis]}
      >
        <div
          className={classNames('horizontal-scroll-fade', {
            'header-fade-left': showLeftFade,
            'header-fade-right': showRightFade,
          })}
        >
          <div ref={scrollContainerRef} className="overflow-x-auto">
            <table
              {...(!isEmptyTable && {
                style: {
                  width: table.getCenterTotalSize(),
                },
              })}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className={classNames({
                      'something-being-dragged': !!currentlyDragged,
                    })}
                  >
                    <SortableContext
                      items={columns as string[]}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => {
                        const currSort = sortingValues?.[header.id];
                        const isSelected = getIsSortApplied(
                          sort,
                          sortingValues?.[header.id],
                        );

                        const isDesc = getIsSortedDescBy(sort, currSort);

                        const isSortable =
                          !currentlyDragged &&
                          isSortableCol(header.id, sortingValues);

                        const hasColumnDropdown =
                          !isPhablet &&
                          !!columnFilterOptions &&
                          (isSortableCol(header.id, sortingValues) ||
                            !!columnFilterOptions[header.id]);

                        const floatingCardContent = (
                          <div
                            className={classNames('floating-card', {
                              clickable: hasColumnDropdown || isSortable,
                              grabbable: !isDragDisabled && !isEmptyTable,
                              'currently-dragged':
                                currentlyDragged === header.id,
                            })}
                            {...(!hasColumnDropdown &&
                              isSortable && {
                                onClick: () => onSort(header.id, isDesc),
                              })}
                          >
                            {!header.isPlaceholder &&
                              flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                          </div>
                        );

                        return (
                          <th
                            key={header.id}
                            {...{
                              colSpan: header.colSpan,
                              style: {
                                width: header.getSize(),
                              },
                            }}
                          >
                            <div
                              className={classNames(header.id, {
                                selected: isSelected,
                              })}
                            >
                              <DraggableCard
                                key={header.id}
                                id={header.id}
                                disabled={isDragDisabled || isEmptyTable}
                              >
                                {hasColumnDropdown ? (
                                  <ColumnSortFilterDropdown
                                    sortingValue={sortingValues?.[header.id]}
                                    filterOption={
                                      columnFilterOptions[header.id]?.options
                                    }
                                    hasCheckboxAll={
                                      columnFilterOptions[header.id]
                                        ?.hasCheckboxAll
                                    }
                                    sortDirectionTranslation={
                                      columnFilterOptions[header.id]
                                        ?.sortDirectionTranslation
                                    }
                                    sort={sort}
                                    updateSort={updateSort}
                                    isDisabled={!!currentlyDragged}
                                  >
                                    {floatingCardContent}
                                  </ColumnSortFilterDropdown>
                                ) : (
                                  floatingCardContent
                                )}
                              </DraggableCard>

                              {!currentlyDragged &&
                                !hasColumnDropdown &&
                                isSortableCol(header.id, sortingValues) && (
                                  <Button
                                    disabled={isEmptyTable}
                                    onClick={() => {
                                      const sortingValue =
                                        sortingValues[header.id];

                                      updateSort(
                                        getSortValue(sortingValue, !isDesc),
                                      );
                                    }}
                                    className={classNames('sorting-button', {
                                      double: !isSelected,
                                      selected: isSelected,
                                      rotated: !isDesc,
                                    })}
                                  >
                                    {isSelected ? (
                                      <ArrowdownIcon />
                                    ) : (
                                      <>
                                        <SortIcon />
                                        <SortIcon />
                                      </>
                                    )}
                                  </Button>
                                )}

                              {!currentlyDragged &&
                                hasSettings(header.id, settingsValues) && (
                                  <Button
                                    id={settingsButtonId}
                                    disabled={isEmptyTable}
                                    onClick={settingsValues[header.id]}
                                    variant="icon"
                                    className={classNames(
                                      'settings-button',
                                      settingsButtonClassName,
                                    )}
                                    icon={SettingsIcon}
                                  />
                                )}

                              {!isResizeDisabled && !isEmptyTable && (
                                <div
                                  {...{
                                    onDoubleClick: () =>
                                      header.column.resetSize(),
                                    onMouseDown: header.getResizeHandler(),
                                    onTouchStart: header.getResizeHandler(),
                                    className: `resizer ${
                                      table.options.columnResizeDirection
                                    } ${
                                      header.column.getIsResizing()
                                        ? 'isResizing'
                                        : ''
                                    }`,
                                    style: {
                                      transform:
                                        columnResizeMode === 'onEnd' &&
                                        header.column.getIsResizing()
                                          ? `translateX(${
                                              (table.options
                                                .columnResizeDirection === 'rtl'
                                                ? -1
                                                : 1) *
                                              (table.getState().columnSizingInfo
                                                .deltaOffset ?? 0)
                                            }px)`
                                          : '',
                                    },
                                  }}
                                />
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </SortableContext>
                  </tr>
                ))}
              </thead>
              <tbody>
                {shouldShowPlaceholder ? (
                  Array(numberOfPlaceholderRowsToRender)
                    .fill(0)
                    .map((_, i) => (
                      <TableRowPlaceholder
                        key={i}
                        numberOfColumns={numberOfColumns}
                      />
                    ))
                ) : isEmptyTable ? (
                  <tr>
                    <td colSpan={numberOfColumns}>
                      <TableEmptyState emptyMessage={emptyMessage} />
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const original = row.original;

                    const hasAdditionalContent = row.getIsExpanded();
                    const cols = row.getVisibleCells();

                    const backgroundColor = getRowBackgroundColor?.(original);
                    const selected = getIsRowSelected?.(original);

                    let colSpanToSkip = 0;

                    return (
                      <Fragment key={row.id}>
                        <tr
                          className={classNames(backgroundColor, {
                            'expandable-row': hasAdditionalContent,
                            selected,
                            clickable: !!onRowClick,
                          })}
                          onClick={() => onRowClick?.(original)}
                          onDoubleClick={() => onRowDoubleClick?.(original)}
                        >
                          {cols.map((cell) => {
                            const colId = cell.column.id;
                            const colSpan = getColSpan?.(original, colId) || 1;
                            const sortAlreadyApplied = sort?.includes(
                              sortingValues?.[colId],
                            );

                            if (colSpanToSkip > 0) {
                              colSpanToSkip--;
                              return null;
                            }

                            colSpanToSkip += colSpan - 1;

                            return (
                              <td
                                key={cell.id}
                                colSpan={colSpan}
                                className={classNames(colId, {
                                  selected: sortAlreadyApplied,
                                })}
                                {...{
                                  style: {
                                    width: cell.column.getSize(),
                                  },
                                }}
                              >
                                {flexRender(cell.column.columnDef.cell, {
                                  ...cell.getContext(),
                                  lead: original,
                                })}
                              </td>
                            );
                          })}
                        </tr>

                        {hasAdditionalContent && (
                          <tr className="expanded-row">
                            <td colSpan={cols.length}>
                              {renderExpandedContent(original)}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DndContext>
      <ResolvePagination totalPages={totalPages} pageParam={pageParam} />
    </div>
  );
}

export default ReorderResizeTable;
